import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle2, Clock, MessageSquare, XCircle, ShieldCheck } from 'lucide-react';
import { onboardingApi } from '../services/api';
import AfricaNendaLogo from '../components/AfricaNendaLogo';
import SupportContact from '../components/SupportContact';
import ResubmitForm from '../components/onboarding/ResubmitForm';

const STAGES = ['REQUESTED', 'UNDER_REVIEW', 'APPROVED'];
const META = {
  REQUESTED:         { label: 'Submitted',         desc: "We've received your application.", Icon: Clock, cls: 'text-blue-600' },
  UNDER_REVIEW:      { label: 'Under review',      desc: 'AfricaNenda is reviewing your application.', Icon: Clock, cls: 'text-amber-600' },
  CHANGES_REQUESTED: { label: 'Changes requested', desc: 'We need a few changes before we can approve.', Icon: MessageSquare, cls: 'text-purple-600' },
  APPROVED:          { label: 'Approved',          desc: 'Your account is ready — check your email for the activation link.', Icon: ShieldCheck, cls: 'text-green-600' },
  REJECTED:          { label: 'Not approved',      desc: 'This application was not approved.', Icon: XCircle, cls: 'text-red-600' },
};

export default function ApplicationStatus() {
  const { token } = useParams();
  const [app, setApp] = useState(null);
  const [error, setError] = useState(null);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    onboardingApi.status(token).then(setApp).catch(e => setError(e.response?.data?.error || e.message));
  }, [token]);

  const m = app ? (META[app.status] || META.REQUESTED) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
        <div className="flex justify-center mb-6"><AfricaNendaLogo className="h-8" /></div>
        {!app && !error && <div className="py-6 text-gray-500 flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>}
        {error && <div className="text-center"><AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" /><p className="text-sm text-gray-600">{error}</p></div>}

        {app && m && (<>
          <div className="text-center">
            <div className={`inline-flex w-14 h-14 rounded-full items-center justify-center mb-3 bg-gray-50 ${m.cls}`}><m.Icon className="w-7 h-7" /></div>
            <p className="text-xs text-gray-500 font-mono">{app.proposedTenantId}</p>
            <h1 className="text-lg font-semibold text-gray-900 mt-1">{m.label}</h1>
            <p className="text-sm text-gray-600 mt-1">{m.desc}</p>
          </div>

          {app.status !== 'REJECTED' && app.status !== 'CHANGES_REQUESTED' && (
            <div className="flex items-center justify-between mt-6">
              {STAGES.map((s, i) => {
                const reached = STAGES.indexOf(app.status) >= i || app.status === 'APPROVED';
                return (
                  <React.Fragment key={s}>
                    <div className="flex flex-col items-center">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${reached ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        {reached ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                      </div>
                      <span className="text-[11px] mt-1 text-gray-500">{META[s].label}</span>
                    </div>
                    {i < STAGES.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${STAGES.indexOf(app.status) > i ? 'bg-green-300' : 'bg-gray-200'}`} />}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {app.reviewNote && (
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-700 font-medium mb-1">Note from the review team</p>
              <p className="text-sm text-amber-900">{app.reviewNote}</p>
            </div>
          )}

          {app.status === 'CHANGES_REQUESTED' && (
            <ResubmitForm app={app} statusToken={token} onDone={(updated) => { setApp(updated); setResent(true); }} />
          )}
          {resent && (
            <div className="mt-5 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              Thanks. Your revised application is back with AfricaNenda for review. We'll email you when there's a decision.
            </div>
          )}

          <div className="mt-6 text-center text-xs text-gray-400">
            {!app.emailVerifiedAt && <p className="text-amber-600 mb-2">Your email isn't verified yet — check your inbox for the verification link.</p>}
            Submitted {app.createdAt}
          </div>
        </>)}
        {(app || error) && (
          <SupportContact className="mt-5 text-center" lead="Questions about your application? Email AfricaNenda at"
            subject={`AfricaNenda IIPS Sandbox application${app?.proposedTenantId ? ` ${app.proposedTenantId}` : ''}`} />
        )}
      </div>
    </div>
  );
}
